"use client";

import React, { useState, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";
import Modal from "./ui/Modal";
import Button from "src/app/components/ui/Button";
import InputField from "./ui/form/Input";
import TextArea from "./ui/form/TextArea";
import { AiOutlineCheckCircle } from "react-icons/ai";

// Zod schema for form validation
const formSchema = z.object({
  to: z
    .string()
    .min(1, { message: "Company email is required" })
    .email({ message: "Invalid company email address" }),
  name: z.string().min(1, { message: "Full name is required" }),
  phone: z
    .string()
    .min(1, { message: "Phone number is required" })
    .regex(/^\d{11}$/, { message: "Phone number must be 11 digits" }),
  email: z
    .string()
    .min(1, { message: "Email is required" })
    .email({ message: "Invalid email address" }),
  message: z
    .string()
    .min(10, { message: "Opinion must be at least 10 characters long" }),

  subject: z
    .string()
    .min(10, { message: "Opinion must be at least 10 characters long" }),
});

// Type derived from Zod schema
type FormData = z.infer<typeof formSchema>;

// Props interface
interface ContactFormProps {
  variant?: boolean;
  email: string;
}

// Constants
const FORM_SUBMITTED_KEY = "formSubmitted";
const INITIAL_FORM_DATA: FormData = {
  to: "",
  name: "",
  phone: "",
  email: "",
  message: "",
  subject: "New Contact Form Submission",
};

const ContactForm: React.FC<ContactFormProps> = ({
  variant = false,
  email,
}) => {
  const [formData, setFormData] = useState<FormData>({
    ...INITIAL_FORM_DATA,
    to: email,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>(
    {}
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState<"success" | "error">("success");

  // Check for prior form submission
  // useEffect(() => {
  //   if (localStorage.getItem(FORM_SUBMITTED_KEY)) {
  //     setModalMessage("You have already submitted the form.");
  //     setModalType("error");
  //     setIsModalOpen(true);
  //   }
  // }, []);

  // Handle input changes
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      // Clear error for the field being edited
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    },
    []
  );

  // Form submission handler
  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      // Prevent duplicate submissions
      if (localStorage.getItem(FORM_SUBMITTED_KEY)) {
        setModalMessage("You have already submitted the form.");
        setModalType("error");
        setIsModalOpen(true);
        return;
      }

      // Validate form data
      try {
        const validatedData = formSchema.parse(formData);
        mutate(validatedData);
      } catch (error) {
        if (error instanceof z.ZodError) {
          const newErrors: Partial<Record<keyof FormData, string>> = {};
          error.errors.forEach((err) => {
            if (err.path[0]) {
              newErrors[err.path[0] as keyof FormData] = err.message;
            }
          });
          setErrors(newErrors);
        }
      }
    },
    [formData]
  );

  // Close modal handler
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // React Query mutation for form submission
  const { mutate, isPending } = useMutation({
    mutationFn: async (data: FormData) => {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BASE_URL}api/v1/contact/company`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to submit form");
      }

      return response.json();
    },
    onSuccess: () => {
      localStorage.setItem(FORM_SUBMITTED_KEY, "true");
      setModalMessage("Form submitted successfully!");
      setModalType("success");
      setIsModalOpen(true);
      setFormData(INITIAL_FORM_DATA);
      setErrors({});
    },
    onError: (error: Error) => {
      setModalMessage(
        error.message || "An error occurred while submitting the form."
      );
      setModalType("error");
      setIsModalOpen(true);
    },
  });

  return (
    <>
      <form
        onSubmit={handleSubmit}
        className="w-full bg-white"
        noValidate // Prevent browser validation
      >
        <div className="grid grid-cols-1 gap-4">
          <InputField
            variant={variant}
            label="Full Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter your name"
            error={errors.name}
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <InputField
              variant={variant}
              label="Phone Number"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Enter your phone number"
              error={errors.phone}
            />
            <InputField
              variant={variant}
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email address"
              error={errors.email}
            />
          </div>
          <TextArea
            variant={variant}
            label="Your Opinion"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Write your message here"
            error={errors.message}
          />
        </div>

        <Button
          type="submit"
          className={`mt-8 block w-max mx-auto px-12 py-3 primary-btn ${
            variant ? "!w-full" : ""
          }`}
          disabled={isPending}
        >
          {isPending ? "Submitting..." : "Submit"}
        </Button>
      </form>

      {isModalOpen && (
        <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
          <div
            className={`text-center ${
              modalType === "success" ? "text-green-500" : "text-red-500"
            }`}
          >
            <AiOutlineCheckCircle className="mx-auto" size={50} />
            <h3 className="mt-4 text-lg font-semibold">{modalMessage}</h3>
            <Button onClick={handleCloseModal} className="mt-4">
              Close
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default ContactForm;
